import {HoistModel, managed, SelectOption, TaskObserver, XH} from '@xh/hoist/core';
import {action, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {Allocation, Portfolio, Security} from '../../../data/DataTypes';
import {FormModel} from '@xh/hoist/cmp/form';
import {numberIs, required} from '@xh/hoist/data';
import {getDefaultOptions} from '../../../../core/ServiceUtils';
import {UserName} from '../../../../core/DataTypes';
import {forEach, groupBy, orderBy} from 'lodash';

export class AddAllocationDialogModel extends HoistModel {
    @observableRef private accessor portfolios: Portfolio[] = [];
    @observableRef private accessor securities: Security[] = [];
    @observableRef private accessor users: UserName[] = [];
    apiTask = TaskObserver.trackLast();
    @observable accessor isOpen = false;
    @managed formModel = this.createFormModel();
    private resolver: (value: Allocation) => void;

    @action
    async openAsync(securityId: number): Promise<Allocation> {
        this.formModel.init({securityId});
        this.resolver = () => {};
        this.isOpen = true;
        await this.loadDomainDataAsync().linkTo(this.apiTask);
        return new Promise(r => (this.resolver = r));
    }

    @action
    close() {
        this.isOpen = false;
        this.resolver(null);
    }

    get portfolioOptions(): SelectOption[] {
        const options: SelectOption[] = [];
        const portfolioGroups = groupBy(this.portfolios, 'userId');
        forEach(portfolioGroups, (pGroup, gValue) => {
            const userId = parseInt(gValue);
            options.push({
                value: userId,
                label: this.users.find(u => u.id == userId).username,
                options: orderBy(
                    pGroup.map(p => ({value: p.id, label: p.portfolioName})),
                    'label'
                )
            });
        });

        return orderBy(options, 'label');
    }

    get securityOptions(): SelectOption[] {
        return orderBy(
            this.securities.map(it => ({value: it.id, label: it.name})),
            'label'
        );
    }

    @action
    save() {
        const allocation = this.formModel.getData() as Allocation;
        this.isOpen = false;
        this.resolver(allocation);
    }

    private async loadDomainDataAsync(): Promise<void> {
        const securities = await XH.secMasterService.searchSecuritiesAsync('%');
        const portfolios = (await XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/RealEstate/portfolios`,
            ...getDefaultOptions()
        })) as Portfolio[];
        const users = await XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/users/names`,
            ...getDefaultOptions()
        });
        runInAction(() => {
            this.portfolios = portfolios.filter(p => p.isActive);
            this.securities = securities;
            this.users = users;
        });
    }

    private createFormModel(): FormModel {
        return new FormModel({
            fields: [
                {name: 'portfolioId', rules: [required]},
                {name: 'securityId', rules: [required]},
                {name: 'allocationPct', rules: [required, numberIs({min: 0.01, max: 1})]}
            ]
        });
    }
}
